import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { CompanyCodeModal } from './CompanyCodeModal.jsx';
import { IntercompanyPostingModal } from './IntercompanyPostingModal.jsx';

export function IntercompanyPage() {
  const { t } = useTranslation();
  const [companyOpen, setCompanyOpen] = useState(false);
  const [postingOpen, setPostingOpen] = useState(false);

  const recon = useQuery({ queryKey: ['finance', 'intercompany', 'recon'], queryFn: () => financeApi.intercompanyReconciliation().then((r) => r.items) });
  const postings = useQuery({ queryKey: ['finance', 'intercompany', 'postings'], queryFn: () => financeApi.listIntercompanyPostings().then((r) => r.items) });
  const companies = recon.data?.companies || [];
  const totals = recon.data?.totals;

  const net = (v) => <span className="mono num" style={{ color: Number(v) < 0 ? 'var(--color-danger, #dc2626)' : Number(v) > 0 ? 'var(--color-success, #059669)' : undefined }}>{formatCurrency(v)}</span>;

  const reconColumns = [
    { key: 'code', label: t('ic.col.company'), render: (r) => <span><span className="mono"><strong>{r.code}</strong></span> {r.name}</span> },
    { key: 'receivable', label: t('ic.col.receivable'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.receivable)}</span> },
    { key: 'payable', label: t('ic.col.payable'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.payable)}</span> },
    { key: 'netv', label: t('ic.col.net'), align: 'right', render: (r) => net(r.net) },
  ];

  const postingColumns = [
    { key: 'doc', label: t('ic.col.doc'), render: (r) => <span className="mono">{r.docNumber}</span> },
    { key: 'date', label: t('ic.col.date'), render: (r) => formatDate(r.postingDate) },
    { key: 'flow', label: t('ic.col.flow'), render: (r) => <span className="mono">{r.fromCompanyCode} → {r.toCompanyCode}</span> },
    { key: 'desc', label: t('ic.col.description'), render: (r) => r.description || '-' },
    { key: 'amount', label: t('ic.col.amount'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.amount)}</span> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('ic.title')}</h1>
          <p className="muted">{t('ic.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => setCompanyOpen(true)}>{t('ic.action.newCompany')}</Button>
          <Button variant="primary" onClick={() => setPostingOpen(true)}>{t('ic.action.newPosting')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('ic.metric.companies')} value={String(companies.length)} />
        <MetricCard label={t('ic.metric.receivable')} value={formatCurrency(totals?.receivable || 0)} />
        <MetricCard label={t('ic.metric.payable')} value={formatCurrency(totals?.payable || 0)} />
        <MetricCard label={t('ic.metric.net')} value={formatCurrency(totals?.net || 0)} trendVariant={Number(totals?.net) === 0 ? 'success' : 'danger'} />
      </div>

      <div className="settings-divider">{t('ic.reconciliation')}</div>
      <Table columns={reconColumns} rows={companies} isLoading={recon.isLoading} emptyMessage={t('ic.noCompanies')} />

      <div className="settings-divider">{t('ic.postings')}</div>
      <Table columns={postingColumns} rows={postings.data || []} isLoading={postings.isLoading} emptyMessage={t('ic.noPostings')} />

      <CompanyCodeModal open={companyOpen} onClose={() => setCompanyOpen(false)} />
      <IntercompanyPostingModal open={postingOpen} onClose={() => setPostingOpen(false)} companies={companies} />
    </section>
  );
}
